import { countryName } from "../../locales/display";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import Panel from "@ui/panel/Panel";
import { CountriesData } from "@ui/utils/CountriesData";
import Signal from "badmfck-signal";
import { Check, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

interface ICountryGroup {
	name: string;
	codes: string[];
}

const COUNTRY_GROUPS: ICountryGroup[] = [
	{
		name: "EU",
		codes: [
			"AT",
			"BE",
			"BG",
			"HR",
			"CY",
			"CZ",
			"DK",
			"EE",
			"FI",
			"FR",
			"DE",
			"GR",
			"HU",
			"IE",
			"IT",
			"LV",
			"LT",
			"LU",
			"MT",
			"NL",
			"PL",
			"PT",
			"RO",
			"SK",
			"SI",
			"ES",
			"SE",
		],
	},
	{
		get name() { return msg("europe"); },
		codes: [
			"AL",
			"AD",
			"AT",
			"BY",
			"BE",
			"BA",
			"BG",
			"HR",
			"CY",
			"CZ",
			"DK",
			"EE",
			"FI",
			"FR",
			"DE",
			"GR",
			"HU",
			"IS",
			"IE",
			"IT",
			"XK",
			"LV",
			"LI",
			"LT",
			"LU",
			"MT",
			"MD",
			"MC",
			"ME",
			"NL",
			"MK",
			"NO",
			"PL",
			"PT",
			"RO",
			"SM",
			"RS",
			"SK",
			"SI",
			"ES",
			"SE",
			"CH",
			"UA",
			"GB",
			"VA",
		],
	},
	{
		name: "EEA",
		codes: [
			"AT",
			"BE",
			"BG",
			"HR",
			"CY",
			"CZ",
			"DK",
			"EE",
			"FI",
			"FR",
			"DE",
			"GR",
			"HU",
			"IE",
			"IT",
			"LV",
			"LT",
			"LU",
			"MT",
			"NL",
			"PL",
			"PT",
			"RO",
			"SK",
			"SI",
			"ES",
			"SE",
			"IS",
			"LI",
			"NO",
		],
	},
	{
		get name() { return msg("britain"); },
		codes: ["GB"],
	},
];

export const S_SELECT_COUNTRIES_POPUP = new Signal<{
	onSuccess: (countries: ICountry[]) => void;
} | null>("S_SELECT_COUNTRIES_POPUP");

const CountryPicker = styled.div`
	display: flex; flex-direction: column; flex: 1; min-height: 0; color: #344054; font-size: 13px;
	button, input { font: inherit; }
	button { cursor: pointer; }
	button:focus-visible, input:focus-visible { outline: 2px solid #6374d9; outline-offset: 2px; }
	.header { padding: 22px 24px 18px; background: #fff; border-bottom: 1px solid #e1e6ef; flex-shrink: 0; }
	.heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
	.eyebrow { font-size: 11px; color: #667085; margin-bottom: 6px; }
	h2 { font-size: 21px; line-height: 1.3; font-weight: 600; margin: 0; color: #182230; }
	.subtitle { color: #667085; margin: 10px 0 0; line-height: 1.5; }
	.close { display: grid; place-items: center; width: 32px; height: 32px; flex: 0 0 32px; border: 1px solid transparent; border-radius: 8px; background: #fff; color: #667085; }
	.close:hover { background: #f5f7fa; border-color: #cdd5e2; }
	.body { display: flex; flex-direction: column; flex: 1; min-height: 0; gap: 16px; background: #f5f7fa; padding: 20px 24px; }
	.section-label { color: #667085; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .03em; margin-bottom: 8px; }
	.groups { display: flex; flex-wrap: wrap; gap: 8px; }
	.group { min-height: 34px; padding: 6px 12px; border: 1px solid #cdd5e2; border-radius: 7px; background: #fff; color: #344054; }
	.group:hover { border-color: #98a2b3; background: #f8fafc; }
	.group[aria-pressed="true"] { background: #eef4ff; color: #175cd3; border-color: #a4bcfd; }
	.group[data-partial="true"] { border-color: #a4bcfd; }
	.group span { margin-left: 8px; color: #667085; font-size: 11px; }
	.search { display: flex; align-items: center; gap: 10px; border: 1px solid #cdd5e2; border-radius: 8px; padding: 0 12px; background: #fff; color: #98a2b3; }
	.search:focus-within { border-color: #6374d9; box-shadow: 0 0 0 2px #6374d91a; }
	.search input { min-width: 0; width: 100%; height: 40px; padding: 0; border: 0; background: transparent; color: #344054; outline: none; }
	.search input::placeholder { color: #98a2b3; }
	.list-card { display: flex; flex-direction: column; flex: 1; min-height: 0; border: 1px solid #cdd5e2; border-radius: 10px; background: #fff; box-shadow: 0 1px 2px #1018280d; overflow: hidden; }
	.list-heading { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 12px 16px; border-bottom: 1px solid #e1e6ef; flex-shrink: 0; }
	.list-heading strong { font-size: 12px; font-weight: 600; }
	.selected-filter { display: flex; align-items: center; gap: 7px; font-size: 12px; color: #667085; cursor: pointer; }
	.selected-filter input { accent-color: #175cd3; }
	.list { overflow-y: auto; min-height: 0; flex: 1; scrollbar-gutter: stable; }
	.row { display: flex; align-items: center; gap: 12px; min-height: 48px; padding: 8px 16px; box-sizing: border-box; border-bottom: 1px solid #edf0f5; cursor: pointer; }
	.row:last-child { border-bottom: 0; }
	.row:hover { background: #f8fafc; }
	.row[data-selected="true"] { background: #eef4ff; }
	.row:focus-within { outline: 2px solid #6374d9; outline-offset: -2px; }
	.row input { position: absolute; opacity: 0; width: 1px; height: 1px; }
	.check { display: grid; place-items: center; width: 18px; height: 18px; flex: 0 0 18px; border: 1px solid #cdd5e2; border-radius: 5px; background: #fff; color: transparent; }
	.row[data-selected="true"] .check { background: #175cd3; border-color: #175cd3; color: #fff; }
	.flag { width: 24px; height: 17px; object-fit: cover; border-radius: 3px; box-shadow: 0 0 0 1px #10182814; }
	.name { flex: 1; min-width: 0; overflow-wrap: anywhere; }
	.code { color: #98a2b3; font-size: 11px; }
	.empty { padding: 32px 16px; color: #667085; text-align: center; line-height: 1.6; }
	.footer { display: flex; align-items: center; gap: 10px; padding: 16px 24px; border-top: 1px solid #e1e6ef; background: #fff; flex-shrink: 0; }
	.count { flex: 1; color: #667085; font-size: 12px; }
	.count strong { color: #344054; }
	.action { min-height: 36px; padding: 8px 16px; border: 1px solid #cdd5e2; border-radius: 7px; background: #fff; color: #344054; font-weight: 500; }
	.action:hover { background: #f5f7fa; }
	.primary { background: #101828; border-color: #101828; color: #fff; }
	.primary:hover { background: #344054; }
	.primary:disabled { cursor: default; opacity: .45; }
	@media (max-width: 480px) { .header, .body, .footer { padding: 16px; } .list-heading { flex-wrap: wrap; } .footer { flex-wrap: wrap; } .count { flex-basis: 100%; } }
`;

export const SelectCountriesPopup = () => {
	useLocale();
	const requestData = S_SELECT_COUNTRIES_POPUP.use();
	const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
	const [search, setSearch] = useState("");
	const [showOnlySelected, setShowOnlySelected] = useState(false);
	const dialog = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!requestData) return;
		setSelectedCodes(new Set());
		setSearch("");
		setShowOnlySelected(false);
		const previousFocus = document.activeElement;
		dialog.current?.focus();
		return () => { if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus(); };
	}, [requestData]);
	if (!requestData) return null;
	const closePopup = () => S_SELECT_COUNTRIES_POPUP.invoke(null);
	const countries = CountriesData.countries;
	const query = search.trim().toLowerCase();
	const visibleCountries = countries.filter(c => (!showOnlySelected || selectedCodes.has(c.alpha2)) && (!query || (c.name.toLowerCase().includes(query) || countryName(c.alpha2, c.name).toLowerCase().includes(query)) || c.alpha2.toLowerCase().includes(query)));
	const selected = selectedCodes.size;
	const toggleCountry = (code: string) => setSelectedCodes(current => {
		const next = new Set(current);
		if (next.has(code)) next.delete(code); else next.add(code);
		return next;
	});
	const toggleGroup = (group: ICountryGroup) => setSelectedCodes(current => {
		const next = new Set(current);
		const codes = countries.filter(c => group.codes.includes(c.alpha2)).map(c => c.alpha2);
		const allSelected = codes.every(code => current.has(code));
		codes.forEach(code => { if (allSelected) next.delete(code); else next.add(code); });
		return next;
	});
	return <Panel decorator="absolute" onClose={closePopup} sx={{ width: "600px", maxWidth: "94vw", height: "min(780px, 88dvh)", maxHeight: "90dvh", padding: "0", borderRadius: "12px", overflow: "hidden", background: "#fff" }}>
		<CountryPicker ref={dialog} role="dialog" aria-modal="true" aria-label={msg("selectCountries")} tabIndex={-1} onKeyDown={event => {
			if (event.key === "Escape") { event.stopPropagation(); closePopup(); }
			if (event.key !== "Tab") return;
			const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input') ?? []);
			const first = controls[0], last = controls.at(-1);
			if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
			else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
		}}>
			<header className="header">
				<div className="eyebrow">{msg("countrySelection")}</div>
				<div className="heading"><h2>{msg("selectCountries")}</h2><button type="button" className="close" aria-label={msg("closeCountrySelection")} onClick={closePopup}><X size={18} /></button></div>
				<p className="subtitle">{msg("chooseIndividualCountriesOrSelectAWholeGroup")}</p>
			</header>
			<div className="body">
				<div><div className="section-label">{msg("quickSelection")}</div><div className="groups">
					{COUNTRY_GROUPS.map(group => {
						const members = countries.filter(c => group.codes.includes(c.alpha2));
						const count = members.filter(c => selectedCodes.has(c.alpha2)).length;
						return <button key={group.name} type="button" className="group" aria-pressed={count === members.length} data-partial={count > 0 && count < members.length} onClick={() => toggleGroup(group)}>{group.name}<span>{count}/{members.length}</span></button>;
					})}
				</div></div>
				<label className="search"><Search size={18} aria-hidden="true" /><input type="search" aria-label={msg("searchCountriesByNameOrCode")} placeholder={msg("searchByCountryNameOrCode")} value={search} onChange={event => setSearch(event.target.value)} /></label>
				<div className="list-card">
					<div className="list-heading"><strong>{msg("countries_398172da")}{visibleCountries.length}</strong><label className="selected-filter"><input type="checkbox" checked={showOnlySelected} onChange={event => setShowOnlySelected(event.target.checked)} />{msg("showOnlySelected")}</label></div>
					<div className="list">
						{visibleCountries.map(country => <label className="row" key={country.alpha2} data-selected={selectedCodes.has(country.alpha2)}>
							<input type="checkbox" checked={selectedCodes.has(country.alpha2)} onChange={() => toggleCountry(country.alpha2)} aria-label={countryName(country.alpha2, country.name)} />
							<span className="check" aria-hidden="true"><Check size={13} strokeWidth={2.5} /></span>
							<img className="flag" src={`https://flagcdn.com/${country.alpha2.toLowerCase()}.svg`} alt="" loading="lazy" />
							<span className="name">{countryName(country.alpha2, country.name)}</span><span className="code">{country.alpha2}</span>
						</label>)}
						{visibleCountries.length === 0 && <div className="empty">{showOnlySelected && !selected ? msg("noCountriesSelectedYetTurnOffTheFilterToSeeAllCountries") : msg("noCountriesMatchYourSearch")}</div>}
					</div>
				</div>
			</div>
			<footer className="footer"><span className="count" aria-live="polite"><strong>{selected}</strong>{msg("countriesSelected")}</span><button type="button" className="action" onClick={closePopup}>{msg("cancel")}</button><button type="button" className="action primary" disabled={!selected} onClick={() => {
				requestData.onSuccess(countries.filter(c => selectedCodes.has(c.alpha2)));
				closePopup();
			}}>{msg("add_9fd728c6")}{selected ? ` (${selected})` : msg("countries_1b076a09")}</button></footer>
		</CountryPicker>
	</Panel>;
};
